/**
 * CardBudgetDialog - Dialog for setting card spending budgets
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Settings2, 
  Loader2,
  Target,
  Bell
} from 'lucide-react';
import { MERCHANT_CATEGORIES, BUDGET_PERIODS, getCategoryIcon, getCategoryColor } from './constants';

const CardBudgetDialog = ({ 
  open, 
  onOpenChange, 
  selectedCard,
  categoryBudgets,
  setCategoryBudgets,
  budgetPeriods,
  setBudgetPeriods,
  alertThreshold,
  setAlertThreshold,
  onSave,
  saving
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-500" />
            Budget Settings
          </DialogTitle>
          <DialogDescription>
            Set spending limits for each category
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Alert Threshold */}
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-amber-600" />
              <span className="font-medium">Alert Threshold</span>
            </div>
            <div className="flex items-center gap-4">
              <Slider
                value={[alertThreshold]}
                onValueChange={(value) => setAlertThreshold(value[0])}
                min={50}
                max={100}
                step={5}
                className="flex-1"
              />
              <span className="text-sm font-bold text-amber-600 w-12">
                {alertThreshold}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Get alerted when spending reaches this percentage
            </p>
          </div>

          {/* Category Budgets */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              Category Budgets
            </Label>
            
            {MERCHANT_CATEGORIES.map((category) => {
              const Icon = getCategoryIcon(category);
              const color = getCategoryColor(category);
              
              return (
                <div 
                  key={category}
                  className="flex items-center gap-3 p-3 rounded-lg border"
                >
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <span className="flex-1 text-sm font-medium">{category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">$</span>
                    <Input
                      type="number"
                      placeholder="No limit"
                      className="w-24 h-8"
                      value={categoryBudgets[category] || ''}
                      onChange={(e) => setCategoryBudgets({
                        ...categoryBudgets,
                        [category]: e.target.value ? parseFloat(e.target.value) : null
                      })}
                    />
                    <Select
                      value={budgetPeriods[category] || 'monthly'}
                      onValueChange={(value) => setBudgetPeriods({
                        ...budgetPeriods,
                        [category]: value
                      })}
                    >
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_PERIODS.map((period) => (
                          <SelectItem key={period.value} value={period.value}>
                            {period.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={saving}
            className="bg-gradient-to-r from-emerald-600 to-teal-600"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Budgets'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CardBudgetDialog;
